<template>
  <div ref="CRENEAUX" class="section">

    <!-- En-tête : chevron + titre + bascule frise / carte -->
    <div class="creneaux-header">
      <v-icon @click="toggleExtended" class="creneaux-chevron">
        {{ extended ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}
      </v-icon>
      <span class="section-titre">Jours travaillés</span>
      <div v-if="extended" class="view-toggle ms-4">
        <v-btn icon variant="text" flat :ripple="false"
               class="toggle-btn" :class="{ active: isTimelineView }" @click="setView(true)">
          <v-icon icon="fas fa-bars" size="16" />
        </v-btn>
        <v-btn icon variant="text" flat :ripple="false"
               class="toggle-btn" :class="{ active: !isTimelineView }" @click="setView(false)">
          <v-icon icon="fas fa-border-all" size="16" />
        </v-btn>
      </div>
    </div>

    <div v-if="extended">

      <!-- Carte résumé + légende -->
      <div class="creneaux-summary-row">
        <div class="creneaux-summary-card">
          <div class="summary-item">
            <div class="summary-item-header">Jours travaillés</div>
            <div class="summary-item-value">{{ totalJours }} {{ totalJours > 1 ? 'jours' : 'jour' }}</div>
          </div>
          <div class="summary-item">
            <div class="summary-item-header">{{ isCachetMode ? 'Cachets' : 'Heures travaillées' }}</div>
            <div class="summary-item-value">
              <template v-if="isCachetMode">{{ totalCachets }} {{ totalCachets > 1 ? 'cachets' : 'cachet' }}</template>
              <template v-else>{{ totalHeures }}</template>
            </div>
          </div>
        </div>
        <div v-if="isTimelineView && legende.length" class="creneaux-legend">
          <div v-for="(activite, index) in legende" :key="index" class="legend-item">
            <span class="legend-dot" :style="{ backgroundColor: activite.color }"></span>
            {{ activite.label }}
          </div>
        </div>
        <div v-if="isTimelineView" class="creneaux-menu">
          <v-menu open-on-hover location="bottom end">
            <template #activator="{ props }">
              <v-icon v-bind="props" class="creneaux-menu-icon" size="20">fas fa-ellipsis-vertical</v-icon>
            </template>
            <v-list density="compact">
              <v-list-item :disabled="!someSelected" @click="deleteSelection">
                <v-list-item-title>Supprimer la sélection</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </div>
      </div>

      <!-- Aucune donnée -->
      <div v-if="creneaux.length === 0" class="empty-list-container">
        <img src="@/assets/SVG/empty-list.svg" class="empty-list-logo"/>
      </div>

      <!-- Vue timeline -->
      <div v-else-if="isTimelineView" class="timeline-view">
        <div class="timeline-row timeline-ruler">
          <div class="timeline-check">
            <v-tooltip text="Tout sélectionner" location="top">
              <template #activator="{ props }">
                <div v-bind="props" class="d-flex align-center">
                  <v-checkbox v-model="allSelected"
                              :indeterminate="someSelected && !allSelected"
                              color="secondary" density="compact" hide-details></v-checkbox>
                </div>
              </template>
            </v-tooltip>
          </div>
          <div class="timeline-date timeline-ruler-text">Dates</div>
          <div class="timeline-frise timeline-axis">
            <span v-for="(tick, i) in hourTicks" :key="i">{{ tick }}h</span>
          </div>
          <div class="timeline-total timeline-ruler-text">Heures travaillées</div>
          <div class="timeline-action"></div>
        </div>

        <div v-for="(item, index) in creneaux" :key="index"
             class="timeline-row clickable"
             @click="selectCreneau(item)">
          <div class="timeline-check" @click.stop>
            <v-checkbox :model-value="isSelected(item)"
                        @update:model-value="(v) => toggleRow(item, v)"
                        color="secondary" density="compact" hide-details></v-checkbox>
          </div>
          <div class="timeline-date" :class="getCreneauValid(item) ? '' : 'error-text'">
            {{ new Date(item.date) }}
          </div>
          <div class="timeline-frise">
            <frise :plages="item.plages" :readOnly="true" :hideHours="true"></frise>
          </div>
          <div class="timeline-total">{{ dayHeures(item) }}</div>
          <div class="timeline-action">
            <v-icon v-if="canEdit" @click.stop="selectCreneau(item)" class="timeline-edit" size="20">fas fa-pencil</v-icon>
            <v-icon v-if="canDeleteCreneau" @click.stop="deleteCreneau(item)" class="timeline-trash" size="20">fas fa-trash-can</v-icon>
          </div>
        </div>
      </div>

      <!-- Vue carte (par jour) — réutilise la CSS des cartes établissement / salarié -->
      <div v-else class="card-list-container">
        <div v-for="(item, index) in creneaux" :key="index" class="pb-2">
          <v-row @click="selectCreneau(item)" class="list-card ma-0">
            <v-col>
              <v-row class="ma-0">
                <div class="card-title" :class="getCreneauValid(item) ? '' : 'error-text'">
                  {{ formatDateLong(item.date) }}
                </div>
              </v-row>
              <div class="seperator"/>
              <v-row class="ma-0">
                <div v-for="(plage, pIndex) in sortPlages(item.plages)" :key="pIndex" class="info-box">
                  <div class="info-text">{{ plageLabel(plage) }}</div>
                </div>
              </v-row>
            </v-col>
          </v-row>
        </div>
      </div>

      <!-- Pied : ajouter des jours (contrat modifiable) -->
      <div v-if="canEdit" class="creneaux-add" @click="addCreneau">
        <span>Ajouter un ou plusieurs jours de travail</span>
        <v-icon size="16">fas fa-plus</v-icon>
      </div>

      <!-- Bouton Modifier (contrat non modifiable) -->
      <div v-if="!canEdit" class="d-flex flex-row-reverse p-3">
        <loader-button :disabled="disabled"
                       :outlined="outlined"
                       :loading="loading"
                       :label="modifyContrat"
                       class="mt-2 ms-2"
                       type="button"
                       @callback="modify">
        </loader-button>
      </div>

      <section-container :dialogOpen="dialogOpen"
                         :extended="true"
                         :section="section"
                         :fiche="fiche"
                         :midSize="true"
                         @closeDialog="closeEditor">
      </section-container>

      <!-- Volet droit : édition des créneaux (réutilise le panneau standard de l'app) -->
      <Transition name="scrim">
        <div v-if="sectionOpen" class="global-scrim" @click="closePanel"></div>
      </Transition>
      <Transition name="slide-panel">
        <div v-if="sectionOpen" class="local-panel-wrap">
          <div class="local-panel col-5">
            <component :is="sectionComponent" :key="panelKey" ref="SECTION_DIALOG"
                       v-bind="sectionProps" v-on="sectionEvents"></component>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script lang="ts">
  import { storeCreneau, storeActivite, storeLieu } from '@/generated/stores';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import SectionContainer from '@/framework/base/components/SectionContainer.vue';
  import SectionHeader from '@/framework/base/components/SectionHeader.vue';
  import AdresseSummary from '@/extend/controls/adresse/AdresseSummary';
  import Frise from '@/framework/base/components/Frise.vue';
  import Contrat, { ContratStatuts } from '@/generated/classes/Contrat';
  import Creneau from '@/generated/classes/Creneau';
  import Section from '@/framework/base/classes/Section';
  import { AppButtons, AppSections, FwkButtons, RessourceAppButton, RessourceAppSection, RessourceFwkButton } from '@/ressources/globalRessources';
  import { Extended } from '@/framework/base/classes/Schema';
  import CreneauxEditor from './CreneauxEditor.vue';
  import CreneauxEditorPanel from './CreneauxEditorPanel.vue';
  import { Alerts } from '@/framework/base/helpers/Alerts';
  import { isMobile } from '@/style/StyleHelpers';
  import { Fiches } from '@/generated/classes/AppSchema';
  import PlageCreneau from '@/generated/classes/PlageCreneau';
  import { FieldStatus } from '@/framework/base/components/FieldInfo';
  import { DateFormat } from '@/framework/base/helpers/DateFormat';
  import { defineComponent, markRaw, type PropType } from "vue";

  export default defineComponent({
    name: 'SectionCreneaux',
    components: {
      LoaderButton,
      SectionContainer,
      SectionHeader,
      AdresseSummary,
      Frise,
      CreneauxEditor,
      CreneauxEditorPanel
    },
    props: {
      hideTitle: {
        type: Boolean,
        required: false,
      },
      contrat: {
        type: Object as PropType<Contrat>,
        required: true,
      },
      moduleId: {
        type: Number,
        required: false,
      },
      creneauId: {
        type: Number,
        required: false,
      },
      dialog: {
        type: Boolean,
        required: false,
      },
      closeSection: {
        type: Boolean,
        required: false,
      },
      extended: {
        type: Boolean,
        required: false,
      },
      fiche: {
        type: String,
        required: false,
      },
    },
    data () {
      return {
        status: new FieldStatus(false, false, 0),
        creneauItems: [] as Creneau[],
        contratId: 0,
        dialogOpen: false,
        loading: false,
        isMobile: isMobile(),
        isTimelineView: true,
        sectionOpen: false,
        sectionComponent: null as any,
        selectedKeys: [] as string[],
        editCreneau: null as Creneau | null,
        panelKey: 0,
        modifyContrat: RessourceFwkButton(FwkButtons.MODIFIER).label,
      };
    },
    computed: {
      newCreneau (): string {
        let res = '';
        if (!this.isMobile) {
          res = RessourceFwkButton(FwkButtons.MODIFIER).label;
        }
        return res;
      },
      ressource (): any {
        return RessourceAppSection(AppSections.CRENEAUX);
      },
      outlined (): boolean {
        return this.contrat.statut == ContratStatuts.SUBMITTED ||
          this.contrat.statut == ContratStatuts.APPROUVED ||
          this.contrat.statut == ContratStatuts.SIGNED ||
          this.contrat.statut == ContratStatuts.CHECKED;
      },
      disabled (): boolean {
        return this.contrat.statut == ContratStatuts.CANCELLED;
      },
      hourTicks (): string[] {
        const res: string[] = [];
        for (let h = 0; h <= 24; h += 2) {
          res.push(h === 24 ? '0' : String(h));
        }
        return res;
      },
      selectedContrat (): Contrat {
        if (this.contrat.id != this.contratId) {
          this.contratId = this.contrat.id;
        }
        return this.contrat;
      },
      creneaux (): Creneau[] {
        this.creneauItems = [];
        const creneaux = storeCreneau.listCreneau.filter(a => a.contratId == this.contrat.id);
        creneaux.forEach((creneau) => {
          const item = this.creneauItems.find(a => a.date.toDateString() == creneau.date.toDateString());
          const plageCreneau = new PlageCreneau();
          plageCreneau.heureDebut = creneau.heureDebut;
          plageCreneau.heureFin = creneau.heureFin;
          plageCreneau.valid = true;
          plageCreneau.creneauId = creneau.id;
          plageCreneau.activiteId = creneau.activiteId;
          plageCreneau.lieuId = creneau.lieuId;
          plageCreneau.color = this.activiteColor(creneau.activiteId) || creneau.color;

          if (item) {
            if (item.plages) {
              item.plages.push(plageCreneau);
            } else {
              item.plages = [plageCreneau]
            }
          } else {
            const clone = creneau.cloneCreneau();
            clone.plages = [plageCreneau];
            this.creneauItems.push(clone)
          }
        })

        this.creneauItems.sort((a: Creneau, b: Creneau) => {
          return new Date(a.date).getTime() - new Date(b.date).getTime();
        });

        return this.creneauItems;
      },
      totalJours (): number {
        return this.creneaux.length;
      },
      totalHeures (): string {
        let total = 0;
        this.creneaux.forEach((item) => {
          total += this.plagesHours(item.plages);
        });
        return this.formatHeures(total);
      },
      isCachetMode (): boolean {
        // Famille « cachets » (Cachet, Répétition en cachet) — même convention que
        // ActiviteSelect.compatibleCi98 (cachets = [1, 2], heures = [5, 6, 7]).
        const cachets = [1, 2];
        return this.creneaux.some((item) => item.plages.some((p) => cachets.includes(p.activiteId)));
      },
      totalCachets (): number {
        const cachets = [1, 2];
        let count = 0;
        this.creneaux.forEach((item) => {
          item.plages.forEach((p) => {
            if (cachets.includes(p.activiteId)) {
              count++;
            }
          });
        });
        return count;
      },
      activiteColorMap (): Record<number, string> {
        // Couleurs distinctes par activité : on garde la couleur d'activité si elle est
        // renseignée ET non déjà utilisée ; sinon (vide ou collision) on prend une couleur
        // de secours unique. Évite que deux activités (ex. heures sup. 25% et 50%) soient identiques.
        const palette = ['#2A97DA', '#58D6DA', '#1C7A7D', '#7EB715', '#E94C49', '#9E6BD8', '#F5A623', '#E91E63'];
        const map: Record<number, string> = {};
        const used = new Set<string>();
        let pi = 0;
        const ids: number[] = [];
        storeCreneau.listCreneau.filter((c) => c.contratId == this.contrat.id).forEach((c) => {
          if (c.activiteId > 0 && !ids.includes(c.activiteId)) {
            ids.push(c.activiteId);
          }
        });
        ids.forEach((id) => {
          const storeColor = storeActivite.getActivite(id).color;
          if (storeColor && !used.has(storeColor)) {
            map[id] = storeColor;
            used.add(storeColor);
          } else {
            while (pi < palette.length && used.has(palette[pi])) pi++;
            const color = palette[pi % palette.length];
            map[id] = color;
            used.add(color);
            pi++;
          }
        });
        return map;
      },
      legende (): { label: string, color: string }[] {
        const res: { label: string, color: string }[] = [];
        const seen = new Set<number>();
        storeCreneau.listCreneau.filter(a => a.contratId == this.contrat.id).forEach((creneau) => {
          if (creneau.activiteId > 0 && !seen.has(creneau.activiteId)) {
            seen.add(creneau.activiteId);
            const activite = storeActivite.getActivite(creneau.activiteId);
            res.push({ label: activite.label, color: this.activiteColorMap[creneau.activiteId] || '#2A97DA' });
          }
        });
        return res;
      },
      canEdit (): boolean {
        return (this.selectedContrat.statut < ContratStatuts.SUBMITTED || this.selectedContrat.statut == ContratStatuts.MODIFIED);
      },
      canDeleteCreneau (): boolean {
        return (this.canEdit && this.creneaux.length > 1);
      },
      someSelected (): boolean {
        return this.selectedKeys.length > 0;
      },
      allSelected: {
        get (): boolean {
          return this.creneaux.length > 0 && this.creneaux.every((i) => this.isSelected(i));
        },
        set (value: boolean) {
          this.selectedKeys = value ? this.creneaux.map((i) => this.rowKey(i)) : [];
        },
      },
      section (): Section {
        const section = new Section();
        section.component = this.$options.components?.CreneauxEditor;
        section.type = 'contrat';
        section.data = this.selectedContrat;
        section.ressource = this.ressource;
        section.picto = 'SectionPlageHorairesPicto';
        return section;
      },
      sectionProps (): any {
        return { contrat: this.selectedContrat, fiche: this.fiche, editCreneau: this.editCreneau };
      },
      sectionEvents (): Record<string, (v?: any) => void> {
        return {
          closeDialog: () => { this.sectionOpen = false; },
          cancelled:  () => { this.sectionOpen = false; },
          saved:      () => { this.sectionOpen = false; this.$emit('updated'); },
          updated:    () => { this.$emit('updated'); },
        };
      },
    },
    methods: {
      toggleExtended (): void {
        this.$emit('extended', new Extended('CRENEAUX', this.fiche as Fiches, !this.extended));
      },
      setView (timeline: boolean): void {
        this.isTimelineView = timeline;
      },
      sortPlages (plages: PlageCreneau[]): PlageCreneau[] {
        return [...plages].sort((a, b) => {
          const current = a.heureDebut ? DateFormat.getHeures(a.heureDebut.toString()) : 0;
          const next = b.heureDebut ? DateFormat.getHeures(b.heureDebut.toString()) : 0;
          return current - next;
        });
      },
      activiteColor (activiteId: number): string {
        return activiteId > 0 ? (this.activiteColorMap[activiteId] || '') : '';
      },
      plageColor (plage: PlageCreneau): string {
        return this.activiteColor(plage.activiteId) || plage.color || '#2A97DA';
      },
      plagesHours (plages: PlageCreneau[]): number {
        let total = 0;
        plages.forEach((plage) => {
          if (plage.heureDebut && plage.heureFin) {
            const start = DateFormat.getHeures(plage.heureDebut.toString());
            const end = DateFormat.getHeures(plage.heureFin.toString());
            if (start != null && end != null && end > start) {
              total += (end - start);
            }
          }
        });
        return total;
      },
      dayHeures (item: Creneau): string {
        return this.formatHeures(this.plagesHours(item.plages));
      },
      formatHeures (value: number): string {
        if (Number.isInteger(value)) {
          return value + 'h';
        }
        return DateFormat.getQuantityHeuresMinutes(value);
      },
      plageLabel (plage: PlageCreneau): string {
        const parts: string[] = [];
        if (plage.lieuId > 0) {
          const lieu = storeLieu.getLieu(plage.lieuId);
          if (lieu && lieu.label) {
            parts.push(lieu.label);
          }
        }
        if (plage.heureDebut && plage.heureFin) {
          parts.push(plage.heureDebut + ' - ' + plage.heureFin);
        }
        if (plage.activiteId > 0) {
          const activite = storeActivite.getActivite(plage.activiteId);
          if (activite && activite.label) {
            parts.push(activite.label);
          }
        }
        return parts.join(' | ');
      },
      formatDateLong (date: Date | string): string {
        return new Date(date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
      },
      selectCreneau (item: Creneau) {
        // Clic sur une ligne -> ouvre le volet droit en ÉDITION du jour sélectionné.
        this.editCreneau = item;
        this.panelKey++;
        this.sectionComponent = markRaw(CreneauxEditorPanel);
        this.sectionOpen = true;
      },
      addCreneau () {
        // Ajout -> volet droit en mode AJOUT (aucun jour à éditer).
        this.editCreneau = null;
        this.panelKey++;
        this.sectionComponent = markRaw(CreneauxEditorPanel);
        this.sectionOpen = true;
      },
      closePanel () {
        // Clic sur le fond central : on ne ferme pas le volet si une heure saisie est
        // invalide (format ou fin ≤ début), pour forcer la correction. Le champ en erreur
        // et le message « Heure invalide » restent ainsi visibles.
        const panel = this.$refs.SECTION_DIALOG as { hasInvalidHours?: () => boolean } | undefined;
        if (panel && typeof panel.hasInvalidHours === 'function' && panel.hasInvalidHours()) {
          return;
        }
        this.sectionOpen = false;
      },
      rowKey (item: Creneau): string {
        return new Date(item.date).toDateString();
      },
      isSelected (item: Creneau): boolean {
        return this.selectedKeys.includes(this.rowKey(item));
      },
      toggleRow (item: Creneau, value: boolean | null) {
        const key = this.rowKey(item);
        if (value) {
          if (!this.selectedKeys.includes(key)) {
            this.selectedKeys.push(key);
          }
        } else {
          this.selectedKeys = this.selectedKeys.filter((k) => k !== key);
        }
      },
      deleteCreneau (creneau: Creneau | null) {
        Alerts.alertDelete().then((res) => {
          if (res) {
            this.loading = true;
            let creneaux: Creneau[] = [];
            if (creneau != null) {
              const creneauIds: number[] = [];
              creneau.plages.forEach((plageCreneau) => {
                creneauIds.push(plageCreneau.creneauId);
              })
              const creneauList = storeCreneau.listCreneau.filter(a => a.contratId == this.contrat.id);
              creneaux = creneauList.filter(a => !creneauIds.includes(a.id))
            }
            Creneau.updateCreneaux(creneaux).then(() => {
              this.loading = false;
              this.$emit('saved');
              this.$emit('closeDialog');
              this.$emit('updated');
            }).catch(() => {
              this.loading = false;
            });
          }
        })
      },
      deleteSelection (): void {
        // Quand TOUTES les lignes sont cochées, on ne peut pas tout supprimer (il doit
        // rester au moins un jour) : message personnalisé + on conserve le 1er jour du tableau.
        const keepFirst = this.allSelected;
        const confirm = keepFirst
          ? Alerts.alertDelete('La suppression est définitive, seul le premier jour de travail sera conservé, Voulez-vous continuer ?')
          : Alerts.alertDelete();
        confirm.then((res) => {
          if (!res) {
            return;
          }
          this.loading = true;
          const creneauList = storeCreneau.listCreneau.filter((a) => a.contratId == this.contrat.id);
          let creneaux: Creneau[];
          if (keepFirst) {
            // Conserver uniquement le premier jour de travail affiché dans le tableau.
            const keepIds = this.creneaux.length > 0
              ? this.creneaux[0].plages.map((plageCreneau) => plageCreneau.creneauId)
              : [];
            creneaux = creneauList.filter((a) => keepIds.includes(a.id));
          } else {
            // ids des créneaux des lignes cochées
            const creneauIds: number[] = [];
            this.creneaux.filter((i) => this.isSelected(i)).forEach((item) => {
              item.plages.forEach((plageCreneau) => {
                creneauIds.push(plageCreneau.creneauId);
              });
            });
            creneaux = creneauList.filter((a) => !creneauIds.includes(a.id));
          }
          Creneau.updateCreneaux(creneaux).then(() => {
            this.loading = false;
            this.selectedKeys = [];
            this.$emit('updated');
          }).catch(() => {
            this.loading = false;
          });
        });
      },
      getCreneauValid (creneau: Creneau): boolean {
        let res = true
        creneau.plages.forEach((item) => {
          if (res && !item.valid) {
            res = item.valid;
          }
        })
        return res;
      },
      modify (): void {
        this.loading = true;
        Contrat.modify(this.contrat).then(() => {
          this.loading = false;
          this.$emit('updated');
        }).catch(() => {
          this.loading = false;
        });
      },
      closeDialog () {
        this.$emit('closeDialog');
      },
      closeEditor () {
        this.dialogOpen = false;
      },
      setExtended (extended: Extended): void {
        this.$emit('extended', extended);
      },
    },
  });
</script>

<style scoped>
/* En-tête */
.creneaux-header {
  display: flex;
  align-items: center;
  padding-bottom: 16px;
}

.creneaux-chevron {
  color: #979797;
  font-size: 16px;
  cursor: pointer;
  margin-right: 12px;
}

.view-toggle {
  display: flex;
  align-items: center;
  padding: 3px;
  border: 1px solid #e6eef0;
  background: #fff;
  height: 37px;
  border-radius: 6px;
  box-sizing: border-box;
  overflow: hidden;
}

.toggle-btn {
  color: #b8e6e4;
  background: transparent !important;
  transition: background 0.15s ease, color 0.15s ease;
  height: 28px !important;
  width: 28px !important;
  min-height: 28px !important;
  min-width: 28px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow: none !important;
}

.toggle-btn:hover {
  background: #eef8f8 !important;
  color: #1c7c7a;
}

.toggle-btn.active {
  background: #dff4f3 !important;
  color: #1c7c7a;
}

/* Carte résumé + légende */
.creneaux-summary-row {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  margin-bottom: 20px;
}

.creneaux-summary-card {
  display: inline-flex;
  gap: 40px;
  border: 1px solid rgb(var(--v-theme-grey95));
  border-radius: 8px;
  padding: 12px 16px;
}

.summary-item-header {
  font-size: 12px;
  color: rgb(var(--v-theme-secondary30));
}

.summary-item-value {
  font-size: 14px;
  font-weight: 400;
  color: #707070;
  margin-top: 4px;
}

.creneaux-legend {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.legend-item {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: rgb(var(--v-theme-secondary20));
}

.legend-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 8px;
}

/* Menu d'actions (3 points) aligné à droite, sur la colonne de la corbeille */
.creneaux-menu {
  margin-left: auto;
  width: 32px;
  display: flex;
  justify-content: center;
}
.creneaux-menu-icon {
  color: rgb(var(--v-theme-grey10));
  cursor: pointer;
}

/* Vue timeline : grille date | frise | total | action */
.timeline-row {
  display: grid;
  grid-template-columns: auto 110px 1fr 130px 64px;
  align-items: center;
  min-height: 48px;
  border-bottom: 1px solid rgb(var(--v-theme-grey95));
}

/* Frise : pleine largeur, sans padding interne, barres centrées */
.timeline-frise :deep(.pt-3) {
  padding-top: 0 !important;
  width: 100%;
}

.timeline-frise :deep(.col-10) {
  flex: 0 0 100% !important;
  max-width: 100% !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.timeline-frise :deep(.col-12) {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.timeline-view :deep(.background-frise) {
  background-color: transparent !important;
}

.timeline-view :deep(.frise-color) {
  height: 12px !important;
  border-radius: 6px !important;
}

/* Lignes de grille verticales toutes les 2h (lignes de données), pleine hauteur */
.timeline-row:not(.timeline-ruler) .timeline-frise {
  align-self: stretch;
  display: flex;
  align-items: center;
  background-image: repeating-linear-gradient(
    to right,
    rgb(var(--v-theme-grey90)) 0,
    rgb(var(--v-theme-grey90)) 1px,
    transparent 1px,
    transparent 8.3333%
  );
}

/* En-tête de règle horaire */
.timeline-ruler {
  border-bottom: none;
  min-height: 28px;
  margin-bottom: 4px;
}

.timeline-axis {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: rgb(var(--v-theme-secondary30));
}

.timeline-ruler-text {
  font-size: 12px;
  font-weight: 400;
  color: rgb(var(--v-theme-secondary30));
}

.timeline-ruler .timeline-total {
  font-weight: 400;
}

/* En-têtes de colonnes (Dates / Heures travaillées) : même bleu que les libellés d'heures. */
.timeline-ruler .timeline-ruler-text {
  color: rgb(var(--v-theme-secondary30));
}

.timeline-date {
  font-size: 13px;
  font-weight: 400;
  color: #707070;
}

.timeline-total {
  text-align: right;
  padding-right: 8px;
  font-size: 13px;
  font-weight: 400;
  color: #707070;
}

.timeline-action {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}

.timeline-check {
  display: flex;
  align-items: center;
  padding-right: 16px;
}
.timeline-check :deep(.v-selection-control) {
  min-height: auto;
}

.clickable {
  cursor: pointer;
}

/* Pied : ajouter des jours */
.creneaux-add {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 16px;
  padding: 16px;
  border: 1px solid rgb(var(--v-theme-grey95));
  border-radius: 8px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  color: rgb(var(--v-theme-secondary20));
  background: #fff;
}

.creneaux-add:hover {
  background-color: rgb(var(--v-theme-secondary98));
}

.btn-trash {
  width: 15px;
  cursor: pointer;
}
.timeline-trash {
  color: rgb(var(--v-theme-grey10));
  cursor: pointer;
}
.timeline-edit {
  color: rgb(var(--v-theme-grey10));
  cursor: pointer;
}
</style>
